import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import DashboardLayout from "../../Layout/DashboardLayout";
import DropdownConfigIndex from "./DropdownConfigIndex";
import LocationConfigIndex from "./Location/LocationConfigIndex";
import FacilityConfigIndex from "./Facility/FacilityConfigIndex";
import TestTypeConfigIndex from "./TestType/TestTypeConfigIndex";
import HolidayCalendarIndex from "./HolidayCalendar/HolidayCalendarIndex";
import ShiftConfigIndex from "./Shift/ShiftConfigIndex";
import GeneralConfigIndex from "./GeneralConfiguration/GeneralConfigIndex";
import CheckListConfigIndex from "./CheckList/CheckListConfigIndex";
import ShiftChangeOverConfigIndex from "./ShiftChangeOver/ShiftChangeOverConfigIndex";
import TemperatureHumidityConfigIndex from "./TemperatureHumidity/TemperatureHumidityConfigIndex";

const TABS = [
  {
    id: "dropdown",
    label: "Dropdown",
    icon: "arrow_drop_down_circle",
    component: DropdownConfigIndex,
  },
  {
    id: "location",
    label: "Location",
    icon: "place",
    component: LocationConfigIndex,
  },
  {
    id: "facility",
    label: "Facilities",
    icon: "apartment",
    component: FacilityConfigIndex,
  },
  {
    id: "testtype",
    label: "Test Type",
    icon: "science",
    component: TestTypeConfigIndex,
  },
  {
    id: "holiday",
    label: "Holiday Calendar",
    icon: "event",
    component: HolidayCalendarIndex,
  },
  {
    id: "shift",
    label: "Shift",
    icon: "schedule",
    component: ShiftConfigIndex,
  },
  {
    id: "checklist",
    label: "CheckList",
    icon: "checklist",
    component: CheckListConfigIndex,
  },
  {
    id: "shiftchangeover",
    label: "Shift Change Over",
    icon: "swap_horiz",
    component: ShiftChangeOverConfigIndex,
  },
  {
    id: "temphumidity",
    label: "Temperature & Humidity",
    icon: "device_thermostat",
    component: TemperatureHumidityConfigIndex,
  },
  {
    id: "general",
    label: "General Config",
    icon: "settings",
    component: GeneralConfigIndex,
  },
];

const NAV_TOP_GAP = 88; // clears TopNav's 64px header + a 24px gap

// Plain CSS `sticky` doesn't reliably engage here — DashboardLayout's
// <main> has overflow-x-hidden, which per the CSS overflow spec forces its
// overflow-y to compute to `auto` (can't leave one axis "visible" while the
// other isn't), making <main> a real scroll container in some browsers
// while the outer page/window still appears to scroll normally in others —
// an ambiguous nested scroll context sticky can't reliably track. This
// tracks the nav's position manually instead and switches it to
// position:fixed once scrolled to NAV_TOP_GAP, so it works no matter which
// element actually ends up scrolling.
function StickyConfigNav({ tabs, activeTab, onSelect }) {
  const containerRef = useRef(null);
  const navRef = useRef(null);
  const [pin, setPin] = useState({ pinned: false, left: 0, width: 236, height: 0 });

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const shouldPin = rect.top <= NAV_TOP_GAP;
      setPin((prev) => {
        if (!shouldPin && !prev.pinned) return prev; // steady state, skip re-render
        return {
          pinned: shouldPin,
          left: rect.left,
          width: rect.width || prev.width,
          height: navRef.current?.offsetHeight || prev.height,
        };
      });
    };
    update();
    // capture:true so this fires for scroll events on ANY scrollable
    // ancestor (they don't bubble normally), not just window/document.
    document.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      document.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex-shrink-0"
      style={{ width: 236, height: pin.pinned ? pin.height : "auto" }}
    >
      <nav
        ref={navRef}
        className="w-[236px] bg-white rounded-2xl border border-[#e5e9e9] p-2 space-y-0.5"
        style={
          pin.pinned
            ? {
                position: "fixed",
                top: NAV_TOP_GAP,
                left: pin.left,
                width: pin.width,
                zIndex: 20,
              }
            : undefined
        }
      >
        {tabs.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onSelect(tab.id)}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-left transition-all
                ${
                  active
                    ? "bg-[#08757C] text-white shadow-sm"
                    : "text-[#3e494a] hover:bg-[#f1f4f4]"
                }`}
            >
              <span
                className={`material-symbols-outlined text-[18px] flex-shrink-0 ${active ? "text-white" : "text-[#9eabac]"}`}
                style={active ? { fontVariationSettings: "'FILL' 1" } : {}}
              >
                {tab.icon}
              </span>
              <span className="truncate">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default function ApplicationConfigPage() {
  const { state } = useLocation();
  const [activeTab, setActiveTab] = useState(state?.tab ?? TABS[0].id);

  const ActiveComponent = TABS.find((t) => t.id === activeTab)?.component;

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#f7fafa]">
        {/* Page header */}
        <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-5">
          <nav className="flex items-center gap-2 text-xs text-[#6e797a] mb-2">
            <span>Administration</span>
            <span className="material-symbols-outlined text-[13px]">
              chevron_right
            </span>
            <span className="font-semibold text-[#08757C]">
              Application Configuration
            </span>
          </nav>
          <h1 className="text-xl sm:text-2xl font-black text-[#181c1d] tracking-tight">
            Application Configuration
          </h1>
        </div>

        {/* Below lg: a horizontal scrollable tab strip — a fixed-width
            sidebar has no room to sit beside content on a phone/tablet.
            At lg+: the original vertical sticky sidebar. Both read/write
            the same activeTab state, so switching breakpoints mid-session
            (resizing a window) never loses your place. */}
        <div className="lg:hidden px-4 sm:px-6 mb-4 -mt-1 overflow-x-auto">
          <div className="flex gap-2 w-max pb-1">
            {TABS.map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors flex-shrink-0
                    ${
                      active
                        ? "bg-[#08757C] text-white shadow-sm"
                        : "bg-white border border-[#e5e9e9] text-[#3e494a]"
                    }`}
                >
                  <span
                    className={`material-symbols-outlined text-[18px] ${active ? "text-white" : "text-[#9eabac]"}`}
                    style={active ? { fontVariationSettings: "'FILL' 1" } : {}}
                  >
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Left nav + content — vertical tab list instead of a horizontal
            strip so adding more config sections never needs scrolling. */}
        {/* No gap here — every tab already wraps its own content in
            px-8 (or similar), so a flex gap on top of that just doubled
            the space between the nav and the content everywhere. */}
        <div className="flex flex-col lg:flex-row lg:items-start px-4 sm:px-6 lg:px-8 pb-8">
          <div className="hidden lg:block">
            <StickyConfigNav
              tabs={TABS}
              activeTab={activeTab}
              onSelect={setActiveTab}
            />
          </div>

          {/* Tab content */}
          <div key={activeTab} className="flex-1 min-w-0 w-full">
            {ActiveComponent && <ActiveComponent embedded />}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
